import { ApiError, uuid } from "@gjwtrack/client";
import type { Integration, Workflow, WorkflowRun } from "@gjwtrack/domain";
import { Play, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Centered } from "../App.tsx";
import { client, useClient } from "../lib/client.ts";
import { absTime, relTimeAgo } from "../lib/format.ts";
import { Button, Checkbox, Input, InputSize } from "../ui/ring.ts";
import { SimpleSelect } from "../ui/SimpleSelect.tsx";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";

const run = async (name: string, input: unknown): Promise<unknown> => {
  try {
    return await client.http.command(name, input, uuid());
  } catch (e) {
    client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
    throw e;
  }
};

const darkTheme = (): boolean => typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;

const TEMPLATE = `// Runs after an issue changes. See the examples for the full API.
export default onChange({
  title: "My rule",
  guard: (ctx) => ctx.changed("State"),
  run: async (ctx) => {
    log(ctx.issue.key, "is now", ctx.issue.fields.State?.name);
  },
});
`;

/** Workflows list: name, scope, enabled, last run. */
export function WorkflowsPage() {
  const [list, setList] = useState<Workflow[] | null>(null);
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const navigate = useNavigate();
  const reload = useCallback(() => client.http.workflows().then(setList).catch((e) => client.pushError("workflows", (e as Error).message)), []);
  useEffect(() => void reload(), [reload]);
  const create = async (): Promise<void> => {
    const projectId = me?.isAdmin ? null : (schema?.data.projects[0]?.id ?? null);
    const wf = (await run("createWorkflow", { name: "New workflow", projectId, code: TEMPLATE, enabled: false })) as Workflow;
    navigate(`/admin/workflows/${wf.id}`);
  };
  return (
    <div className="max-w-5xl">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Workflows</h1>
        <div className="flex-1" />
        <Button primary icon={Plus} onClick={() => void create()}>
          New workflow
        </Button>
      </div>
      {list === null ? (
        <div className="text-muted">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-muted">No workflows yet.</div>
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              <th className="border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase">Name</th>
              <th className="border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase">Scope</th>
              <th className="border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase">Enabled</th>
              <th className="border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase">Updated</th>
            </tr>
          </thead>
          <tbody>
            {list.map((w) => (
              <tr key={w.id}>
                <td className="border-b border-line px-3 py-1.5">
                  <Link to={`/admin/workflows/${w.id}`} className="text-value hover:underline">{w.name}</Link>
                </td>
                <td className="border-b border-line px-3 py-1.5 text-muted">{w.projectId === null ? "All projects" : (schema?.projectsById.get(w.projectId)?.name ?? w.projectId)}</td>
                <td className="border-b border-line px-3 py-1.5">
                  <Checkbox checked={w.enabled} onChange={(e) => void run("updateWorkflow", { workflowId: w.id, enabled: e.target.checked }).then(reload)} />
                </td>
                <td className="border-b border-line px-3 py-1.5 text-muted" title={absTime(w.updatedAt)}>{relTimeAgo(w.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-6 max-w-prose text-muted">
        A workflow is a script with rules: <code>onChange</code> runs after an issue changes, <code>onWebhook</code> when GitLab sends a merge-request event, <code>onSchedule</code> on a cron,
        <code>onAction</code> adds a button to the issue page's ⋯ menu (<code>title</code> is the label), and <code>onValidate</code> runs before a write: throw to reject it with your message.
        Scripts run in a sandbox with a 5 s CPU budget and 64 MB; they talk to the tracker and GitLab only through <code>ctx.issue</code>, <code>gitlab</code> and <code>http</code>.
      </p>
    </div>
  );
}

/** One workflow: code editor, scope, enable, dry run against an issue, run log. */
export function WorkflowPage() {
  const { id = "" } = useParams();
  const wfId = Number(id);
  const [wf, setWf] = useState<Workflow | null>(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [testKey, setTestKey] = useState("");
  const [testOut, setTestOut] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    const list = await client.http.workflows();
    const found = list.find((w) => w.id === wfId) ?? null;
    setWf(found);
    if (found) {
      setCode(found.code);
      setName(found.name);
      setRuns(await client.http.workflowRuns(found.id));
    }
  }, [wfId]);
  useEffect(() => {
    reload().catch((e) => client.pushError("workflow", (e as Error).message));
    const timer = setInterval(() => client.http.workflowRuns(wfId).then(setRuns).catch(() => {}), 5000);
    return () => clearInterval(timer);
  }, [reload, wfId]);

  if (!wf) return <Centered>Loading…</Centered>;
  const dirty = code !== wf.code || name !== wf.name;
  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await run("updateWorkflow", { workflowId: wf.id, code, name: name.trim() || wf.name });
      await reload();
    } finally {
      setBusy(false);
    }
  };
  const test = async (): Promise<void> => {
    const issues = await client.http.byKeys([testKey.trim()]);
    const issue = issues[0];
    if (!issue) {
      setTestOut(`No issue ${testKey}`);
      return;
    }
    if (dirty) await save();
    setBusy(true);
    try {
      const r = (await run("testWorkflow", { workflowId: wf.id, rule: "default", issueId: issue.id })) as { ok: boolean; guard: boolean; log: string; error: string | null; durationMs: number; calls: { name: string; args: unknown }[] };
      setTestOut(
        [
          r.error ? `ERROR: ${r.error}` : r.guard ? `ran in ${r.durationMs} ms (dry run: nothing was changed)` : "guard returned false: rule skipped",
          r.log && `--- log\n${r.log}`,
          r.calls.length && `--- host calls\n${r.calls.map((c) => `${c.name} ${JSON.stringify(c.args)}`).join("\n")}`,
        ]
          .filter(Boolean)
          .join("\n"),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-5xl">
      <div className="mb-1 text-xs text-muted">
        <Link to="/admin/workflows" className="hover:text-ink">Workflows</Link> / {wf.name}
      </div>
      <div className="mb-3 flex items-center gap-2">
        <Input inputClassName="text-lg font-semibold" value={name} onChange={(e) => setName(e.target.value)} />
        <SimpleSelect
          value={wf.projectId === null ? "" : String(wf.projectId)}
          placeholder="All projects"
          options={[{ value: "", label: "All projects" }, ...(schema?.data.projects ?? []).map((p) => ({ value: String(p.id), label: p.name }))]}
          onChange={(v) => void run("updateWorkflow", { workflowId: wf.id, projectId: v ? Number(v) : null }).then(reload)}
          disabled={!me?.isAdmin}
        />
        <Checkbox label="Enabled" checked={wf.enabled} onChange={(e) => void run("updateWorkflow", { workflowId: wf.id, enabled: e.target.checked }).then(reload)} />
        <div className="flex-1" />
        <Button danger icon={Trash2} onClick={() => confirm(`Delete workflow "${wf.name}"?`) && void run("deleteWorkflow", { workflowId: wf.id }).then(() => navigate("/admin/workflows"))}>
          Delete
        </Button>
        <Button primary disabled={!dirty || busy} onClick={() => void save()}>
          Save
        </Button>
      </div>
      <div
        className="overflow-hidden rounded border border-line"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "s") {
            e.preventDefault();
            void save();
          }
        }}
      >
        <CodeMirror value={code} height="60vh" theme={darkTheme() ? "dark" : "light"} extensions={[javascript()]} onChange={setCode} basicSetup={{ lineNumbers: true, foldGutter: true, autocompletion: true, highlightActiveLine: true, tabSize: 2 }} />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Input inputClassName="font-mono" placeholder="DRIVE-12" value={testKey} onChange={(e) => setTestKey(e.target.value)} />
        <Button icon={Play} disabled={!testKey.trim() || busy} onClick={() => void test()}>
          Dry run against this issue
        </Button>
        <span className="text-xs text-muted">Runs the default rule with host calls recorded, not executed.</span>
      </div>
      {testOut && <pre className="mt-2 overflow-x-auto rounded border border-line bg-surface p-3 text-xs whitespace-pre-wrap">{testOut}</pre>}

      <h2 className="mt-8 mb-2 text-sm font-medium text-muted uppercase">Recent runs</h2>
      {runs.length === 0 ? (
        <div className="text-muted">No runs yet.</div>
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              {["When", "Rule", "Trigger", "Issue", "Status", "Time", "Output"].map((h) => (
                <th key={h} className="border-b border-line px-2 py-1 text-left text-xs font-medium text-muted uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="border-b border-line px-2 py-1 text-muted whitespace-nowrap" title={absTime(r.startedAt)}>{relTimeAgo(r.startedAt)}</td>
                <td className="border-b border-line px-2 py-1 font-mono text-xs">{r.rule}</td>
                <td className="border-b border-line px-2 py-1 text-muted">{r.trigger}</td>
                <td className="border-b border-line px-2 py-1">{r.issueId !== null && <IssueLink id={r.issueId} />}</td>
                <td className="border-b border-line px-2 py-1">
                  <span className={`chip ${r.status === "ok" ? "chip-2" : r.status === "skipped" ? "" : "chip-3"}`}>{r.status}</span>
                </td>
                <td className="border-b border-line px-2 py-1 text-muted tabular-nums">{r.durationMs} ms</td>
                <td className="border-b border-line px-2 py-1">
                  {r.error && <div className="text-danger">{r.error}</div>}
                  {r.log && <pre className="text-xs whitespace-pre-wrap text-muted">{r.log}</pre>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function IssueLink({ id }: { id: number }) {
  const issue = useClient((s) => s.display[id]);
  const schema = useClient((s) => s.schema);
  useEffect(() => {
    if (!issue) void client.ensureIssue(id);
  }, [id, issue]);
  if (!issue || !schema) return <span className="text-muted">#{id}</span>;
  const key = `${schema.projectsById.get(issue.projectId)?.key ?? issue.projectId}-${issue.number}`;
  return <Link to={`/issue/${key}`} className="font-mono text-xs text-value hover:underline">{key}</Link>;
}

/** GitLab integrations: base URL, token, webhook secret and the URL to paste into GitLab. */
export function IntegrationsPage() {
  const [list, setList] = useState<Integration[] | null>(null);
  const [form, setForm] = useState({ name: "GitLab", baseUrl: "https://gitlab.com", token: "" });
  const reload = useCallback(() => client.http.integrations().then(setList).catch((e) => client.pushError("integrations", (e as Error).message)), []);
  useEffect(() => void reload(), [reload]);
  return (
    <div className="max-w-4xl">
      <h1 className="mb-4 text-xl font-semibold tracking-tight">Integrations</h1>
      {list === null ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <div className="flex flex-col gap-4">
          {list.map((i) => (
            <div key={i.id} className="rounded border border-line bg-surface p-4">
              <div className="mb-2 flex items-center gap-2">
                <b>{i.name}</b>
                <span className="chip">gitlab</span>
                <span className="text-muted">{i.baseUrl}</span>
                <div className="flex-1" />
                <Button danger icon={Trash2} onClick={() => confirm(`Remove ${i.name}?`) && void run("deleteIntegration", { integrationId: i.id }).then(reload)}>
                  Remove
                </Button>
              </div>
              <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-[13px]">
                <dt className="text-muted">Webhook URL</dt>
                <dd className="font-mono break-all select-all">{location.origin}/hooks/gitlab/{i.id}</dd>
                <dt className="text-muted">Secret token</dt>
                <dd className="font-mono select-all">{i.webhookSecret}</dd>
                <dt className="text-muted">Access token</dt>
                <dd>
                  {i.hasToken ? "set" : "missing"} ·{" "}
                  <Button inline onClick={() => { const t = prompt("New project access token"); if (t) void run("updateIntegration", { integrationId: i.id, token: t }).then(reload); }}>
                    replace
                  </Button>
                </dd>
                <dt className="text-muted">GitLab events</dt>
                <dd className="text-muted">Merge request events. Put the webhook URL and secret token into the GitLab project's Settings → Webhooks.</dd>
              </dl>
            </div>
          ))}
          <form
            className="flex flex-wrap items-end gap-2 rounded border border-line bg-surface p-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run("createIntegration", { kind: "gitlab", name: form.name, baseUrl: form.baseUrl, token: form.token, providerProjects: null }).then(() => {
                setForm({ name: "GitLab", baseUrl: "https://gitlab.com", token: "" });
                return reload();
              });
            }}
          >
            <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Input label="GitLab URL" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} required />
            <div className="min-w-64 flex-1">
              <Input label="Project access token (api scope)" size={InputSize.FULL} inputClassName="font-mono" type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} required />
            </div>
            <Button primary type="submit" icon={Plus}>
              Add GitLab
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
